class StickyAddToCart {
  constructor() {
    this.stickyBar = document.querySelector('.sticky-add-to-cart');
    this.productForm = document.querySelector('[data-product-form]') || document.querySelector('form[action*="/cart/add"]');
    this.isVisible = false;
    this.scrollThreshold = 400;
    
    if (this.stickyBar) {
      this.init();
    }
  }

  init() {
    this.setupScrollThreshold();
    this.setupScrollListener();
    this.setupVariantListener();
    this.setupCartActions();
    this.syncWithMainForm();
    this.updateProductPrice();
  }

  setupScrollThreshold() {
    const threshold = this.stickyBar.dataset.scrollThreshold;
    if (threshold) {
      this.scrollThreshold = parseInt(threshold);
    }
  }

  setupScrollListener() {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const shouldShow = window.pageYOffset > this.scrollThreshold;
          
          if (shouldShow && !this.isVisible) {
            this.show();
          } else if (!shouldShow && this.isVisible) {
            this.hide();
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
  }

  setupVariantListener() {
    // Listen for variant changes from the main product form
    if (this.productForm) {
      const variantSelects = this.productForm.querySelectorAll('[name="id"]');
      const optionInputs = this.productForm.querySelectorAll('input[name*="options"], select[name*="options"]');
      
      // Listen to variant ID changes
      variantSelects.forEach(select => {
        select.addEventListener('change', () => {
          this.updateVariant(select.value);
          this.updateProductPrice(select.value);
        });
      });
      
      // Listen to option changes
      optionInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.updateVariantInfo();
        });
      });
    }
  }

  setupCartActions() {
    const cartBtn = this.stickyBar.querySelector('[data-action="add-to-cart"]');
    const buyNowBtn = this.stickyBar.querySelector('[data-action="buy-now"]');

    if (cartBtn) {
      cartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.buyNow();
      });
    }
  }

  async addToCart() {
    const cartBtn = this.stickyBar.querySelector('[data-action="add-to-cart"]');
    const formData = this.getFormData();
    
    if (!formData.get('id')) {
      this.showError('Please select a variant');
      return;
    }

    this.setButtonLoading(cartBtn, true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new URLSearchParams(formData)
      });

      if (response.ok) {
        const result = await response.json();
        this.showSuccess('Added to cart!');
        
        // Trigger cart drawer or notification
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { item: result }
        }));
        
        // Update cart count if exists
        this.updateCartCount();
        
      } else {
        const error = await response.json();
        this.showError(error.message || 'Unable to add to cart');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.setButtonLoading(cartBtn, false);
    }
  }

  async buyNow() {
    const buyNowBtn = this.stickyBar.querySelector('[data-action="buy-now"]');
    const formData = this.getFormData();
    
    if (!formData.get('id')) {
      this.showError('Please select a variant');
      return;
    }

    this.setButtonLoading(buyNowBtn, true);

    try {
      // Add to cart first
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new URLSearchParams(formData)
      });

      if (response.ok) {
        // Redirect to checkout
        window.location.href = '/checkout';
      } else {
        const error = await response.json();
        this.showError(error.message || 'Unable to proceed to checkout');
      }
    } catch (error) {
      console.error('Buy now error:', error);
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.setButtonLoading(buyNowBtn, false);
    }
  }

  getFormData() {
    const form = this.stickyBar.querySelector('.sticky-add-to-cart__form');
    return new FormData(form);
  }

  setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.classList.add('sticky-add-to-cart__btn--loading');
      button.disabled = true;
    } else {
      button.classList.remove('sticky-add-to-cart__btn--loading');
      button.disabled = false;
    }
  }

  updateVariant(variantId) {
    const variantInput = this.stickyBar.querySelector('.sticky-add-to-cart__variant-id');
    if (variantInput) {
      variantInput.value = variantId;
    }
    
    this.updateAvailability(variantId);
    this.updateProductPrice(variantId);
  }

  updateVariantInfo() {
    const variantInfoElement = this.stickyBar.querySelector('.sticky-add-to-cart__selected-variant');
    if (!variantInfoElement || !this.productForm) return;

    const selectedOptions = [];
    const optionInputs = this.productForm.querySelectorAll('input[name*="options"]:checked, select[name*="options"]');
    
    optionInputs.forEach(input => {
      if (input.value && input.value !== '') {
        selectedOptions.push(input.value);
      }
    });

    variantInfoElement.textContent = selectedOptions.join(' / ');
  }

  updateProductPrice(variantId = null) {
    const priceElement = this.stickyBar.querySelector('[data-product-price]');
    if (!priceElement) return;

    // Get variant data from the page or form
    let selectedVariantId = variantId;
    if (!selectedVariantId) {
      const variantInput = this.stickyBar.querySelector('.sticky-add-to-cart__variant-id');
      selectedVariantId = variantInput ? variantInput.value : null;
    }

    // Try to get variant data from page
    if (window.product && window.product.variants) {
      const selectedVariant = window.product.variants.find(v => v.id == selectedVariantId);
      if (selectedVariant) {
        const formattedPrice = this.formatMoney(selectedVariant.price);
        priceElement.textContent = formattedPrice;
        return;
      }
    }

    // Fallback: try to get price from main product form
    if (this.productForm) {
      const priceElements = this.productForm.querySelectorAll('[data-price], .price');
      if (priceElements.length > 0) {
        const priceText = priceElements[0].textContent.trim();
        // Extract price number and reformat with peso
        const priceMatch = priceText.match(/[\d,]+\.?\d*/);
        if (priceMatch) {
          const priceValue = parseFloat(priceMatch[0].replace(/,/g, ''));
          const formattedPrice = this.formatMoney(priceValue * 100); // Convert to cents
          priceElement.textContent = formattedPrice;
        }
      }
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count in header if exists
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  formatMoney(cents) {
    // Format without trailing zeros
    const pesos = cents / 100;
    let formatted;
    
    if (pesos % 1 === 0) {
      // No decimal places needed
      formatted = pesos.toFixed(0);
    } else {
      // Keep decimal places but remove trailing zeros
      formatted = pesos.toFixed(2).replace(/\.?0+$/, '');
    }
    
    // Use Philippine peso sign
    return `₱${formatted}`;
  }

  updateAvailability(variantId) {
    // Check variant availability and update button states
    const cartBtn = this.stickyBar.querySelector('[data-action="add-to-cart"]');
    const buyNowBtn = this.stickyBar.querySelector('[data-action="buy-now"]');
    
    if (variantId && variantId !== '') {
      cartBtn.disabled = false;
      buyNowBtn.disabled = false;
    } else {
      cartBtn.disabled = true;
      buyNowBtn.disabled = true;
    }
  }

  syncWithMainForm() {
    if (!this.productForm) return;

    // Sync initial values
    const mainVariantInput = this.productForm.querySelector('[name="id"]');
    
    if (mainVariantInput) {
      this.updateVariant(mainVariantInput.value);
    }
  }

  showSuccess(message) {
    this.showNotification(message, 'success');
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showNotification(message, type) {
    // Create a simple notification
    const notification = document.createElement('div');
    notification.className = `sticky-add-to-cart__notification sticky-add-to-cart__notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      padding: 1rem 1.5rem;
      border-radius: 6px;
      font-size: 1.4rem;
      font-weight: 500;
      ${type === 'success' 
        ? 'background-color: #d4edda; color: #155724; border: 1px solid #c3e6cb;' 
        : 'background-color: #f8d7da; color: #721c24; border: 1px solid #f5c6cb;'
      }
      animation: slideInRight 0.3s ease;
    `;

    document.body.appendChild(notification);

    // Auto remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  }

  show() {
    this.stickyBar.classList.add('sticky-add-to-cart--visible');
    this.isVisible = true;
  }

  hide() {
    this.stickyBar.classList.remove('sticky-add-to-cart--visible');
    this.isVisible = false;
  }
}

// CSS for notifications
const notificationStyles = `
@keyframes slideInRight {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes slideOutRight {
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(100%); opacity: 0; }
}
`;

// Add notification styles to head
if (!document.querySelector('#sticky-cart-notification-styles')) {
  const style = document.createElement('style');
  style.id = 'sticky-cart-notification-styles';
  style.textContent = notificationStyles;
  document.head.appendChild(style);
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new StickyAddToCart();
});

// Also handle dynamic content loading
document.addEventListener('shopify:section:load', () => {
  new StickyAddToCart();
});